import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/ui/skeleton'
import { Settings } from '@/interfaces/Settings'
import { formatCurrency } from '@/lib/formats/formatCurrency'
import { components } from '@invo.space/shared'
import { Currency } from '@/types/Currency'

import { InvoicingPreviewBankAccounts } from './InvoicingPreviewBankAccounts'
import { InvoicingPreviewCurrencies } from './InvoicingPreviewCurrencies'
import { InvoicingPreviewFootnote } from './InvoicingPreviewFootnote'
import { InvoicingPreviewSequences } from './InvoicingPreviewSequences'
import { InvoicingPreviewSupplier } from './InvoicingPreviewSupplier'
import { InvoicingPreviewWatermark } from './InvoicingPreviewWatermark'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

type DocumentSequence = components['schemas']['DocumentSequence'] & {
  initialSequence?: string
}

export interface InvoicingPreviewProps {
  settings?: Settings
  bankAccounts?: InvoicingBankAccount[]
  currencies?: Currency[]
  invoiceSequence?: DocumentSequence | null
  isLoading?: boolean
  onMutateSettings: () => void
  onMutateBankAccounts: () => void
  onMutateInvoiceSequences: () => void
  refSource?: string | null
}

const formatAccountNumber = (account: InvoicingBankAccount) => {
  if (account.accountNumber && account.bankCode) {
    return `${account.accountNumber}/${account.bankCode}`
  }
  return account.accountNumber || account.iban || ''
}

const SAMPLE_TOTAL_WITH_TAX = 1210
const SAMPLE_TOTAL_WITHOUT_TAX = 1000

export const InvoicingPreview = ({
  settings,
  bankAccounts = [],
  currencies = [],
  invoiceSequence,
  isLoading,
  onMutateSettings,
  onMutateBankAccounts,
  onMutateInvoiceSequences,
  refSource,
}: InvoicingPreviewProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings' })

  const defaultAccount =
    bankAccounts.find((a) => a.isDefault) ?? bankAccounts[0]
  const primaryCurrency = settings?.primaryCurrency || 'CZK'
  const documentNumber =
    invoiceSequence?.initialSequence ?? `${moment().format('YYYY')}0001`
  const isVatPayer =
    settings?.participant?.vatPayer === 'vatPayer' ||
    settings?.vatPayer === 'vatPayer'
  const sampleTotal = isVatPayer
    ? SAMPLE_TOTAL_WITH_TAX
    : SAMPLE_TOTAL_WITHOUT_TAX

  if (isLoading) {
    return (
      <div className="w-full">
        <Skeleton className="mx-auto aspect-[210/297] w-full max-w-3xl rounded-lg" />
      </div>
    )
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="mx-auto flex max-w-3xl flex-col rounded-lg border bg-background p-4 text-foreground sm:min-h-[842px] sm:p-10">
        {/* Header */}
        <div className="mb-5 flex justify-between">
          <div className="hidden flex-1 sm:block" />
          <InvoicingPreviewSequences onMutate={onMutateInvoiceSequences}>
            <div className="border-b border-foreground pb-2 text-right text-xl font-bold">
              {t('invoicing.preview.documentTitle')}&nbsp;{documentNumber}
            </div>
          </InvoicingPreviewSequences>
        </div>

        {/* Participants */}
        <div className="mb-4 grid gap-6 sm:grid-cols-2 sm:gap-4">
          <InvoicingPreviewSupplier
            participant={settings?.participant}
            participantId={settings?.participantId}
            onSuccess={onMutateSettings}
            refSource={refSource}
          />

          <div className="text-muted-foreground">
            <h2 className="text-sm font-bold text-foreground">Odběratel</h2>
            <div className="mt-1 space-y-0.5 text-[10pt]">
              <p className="font-bold">
                {t('invoicing.preview.sample.customerName')}
              </p>
              <p>Vzorová 123</p>
              <p>110 00, Praha</p>
              <p>Česká republika</p>
              <p>IČ: 12345678</p>
              <p>DIČ: CZ12345678</p>
            </div>
          </div>
        </div>

        {/* Payment strip */}
        <div className="my-2 border-y border-muted-foreground/40 py-1.5">
          <div className="grid gap-2 text-[10pt] sm:grid-cols-2 sm:gap-4">
            <div>
              <table className="w-full sm:w-[90%]">
                <tbody>
                  <tr>
                    <td>Způsob platby:</td>
                    <td className="w-[60%] text-right">Převodem</td>
                  </tr>
                  <tr>
                    <td>Číslo účtu:</td>
                    <td className="text-right">
                      <InvoicingPreviewBankAccounts
                        bankAccounts={bankAccounts}
                        onMutate={onMutateBankAccounts}
                      >
                        <span
                          className={
                            defaultAccount
                              ? 'hover:underline'
                              : 'text-muted-foreground italic'
                          }
                        >
                          {defaultAccount
                            ? formatAccountNumber(defaultAccount)
                            : t('invoicing.preview.bankAccounts.empty')}
                        </span>
                      </InvoicingPreviewBankAccounts>
                    </td>
                  </tr>
                  <tr>
                    <td>Variabilní symbol:</td>
                    <td className="text-right">{documentNumber}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <table className="w-full">
                <tbody>
                  <tr>
                    <td>Datum vystavení:</td>
                    <td className="w-[60%] text-right">
                      {moment().format('D.M.YYYY')}
                    </td>
                  </tr>
                  <tr>
                    <td>Datum splatnosti:</td>
                    <td className="text-right">
                      {moment().add(14, 'days').format('D.M.YYYY')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <table className="my-2 w-full min-w-[480px] text-sm">
            <thead>
              <tr className="border-b border-t border-muted-foreground/40">
                <th className="px-2 py-1.5 text-left font-normal">Položka</th>
                <th className="px-2 py-1.5 text-right font-normal">Množství</th>
                <th className="px-2 py-1.5 text-right font-normal">
                  {isVatPayer ? 'Cena za MJ' : 'Cena/jedn.'}
                </th>
                {isVatPayer && (
                  <th className="px-2 py-1.5 text-right font-normal">DPH</th>
                )}
                <th className="px-2 py-1.5 text-right font-normal">
                  {isVatPayer ? 'Celkem bez DPH' : 'Celkem'}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-muted-foreground/40">
                <td className="px-2 py-3">
                  {t('invoicing.preview.sample.itemName')}
                </td>
                <td className="px-2 py-3 text-right">1</td>
                <td className="px-2 py-3 text-right">
                  {formatCurrency(1000, primaryCurrency)}
                </td>
                {isVatPayer && <td className="px-2 py-3 text-right">21 %</td>}
                <td className="px-2 py-3 text-right">
                  {formatCurrency(1000, primaryCurrency)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* VAT summary + total */}
        <div className="flex flex-row-reverse justify-between">
          <div>
            {isVatPayer && (
              <table className="w-full text-right text-sm">
                <tbody>
                  <tr>
                    <td className="px-2">Celkem bez DPH</td>
                    <td className="px-2">
                      {formatCurrency(1000, primaryCurrency)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2">DPH 21 %</td>
                    <td className="px-2">
                      {formatCurrency(210, primaryCurrency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            <InvoicingPreviewCurrencies
              settings={settings!}
              currencies={currencies}
              onMutate={onMutateSettings}
            >
              <div className="mt-2 border-t border-muted-foreground/40 pr-3 text-right text-base font-bold">
                {formatCurrency(sampleTotal, primaryCurrency)}
              </div>
            </InvoicingPreviewCurrencies>
          </div>
        </div>

        {/* Watermark */}
        <div className="mt-4">
          <InvoicingPreviewWatermark
            signature={settings?.signature}
            onMutate={onMutateSettings}
          >
            {settings?.signature ? (
              <img
                src={
                  import.meta.env.VITE_BACKEND_URL +
                  '/media/watermark/' +
                  settings.signature
                }
                alt=""
                className="max-w-[240px] object-contain hover:opacity-80"
              />
            ) : (
              <div className="flex h-20 max-w-[240px] items-center justify-center text-xs text-muted-foreground italic">
                {t('invoicing.preview.watermark.placeholder')}
              </div>
            )}
          </InvoicingPreviewWatermark>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-8 text-center">
          <div className="mb-3 text-left">
            <InvoicingPreviewFootnote
              footnote={settings?.footnote}
              onMutate={onMutateSettings}
            />
          </div>
          <p className="text-[10pt] text-muted-foreground">
            Vytvořeno pomocí{' '}
            <a
              href="https://invo.space"
              target="_blank"
              rel="noreferrer"
              className="text-foreground no-underline"
            >
              invo.space
            </a>
          </p>
          {settings?.participant?.phone && (
            <p className="text-[10pt]">tel.: {settings.participant.phone}</p>
          )}
          {settings?.participant?.email && (
            <p className="text-[10pt]">
              e-mail:{' '}
              <a
                href={`mailto:${settings.participant.email}`}
                className="text-foreground no-underline"
              >
                {settings.participant.email}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
